import React, { ChangeEvent, useEffect, useMemo, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { ISystem } from '../../ts/interface/system.interface';
import { IMode } from '../../ts/interface/mode.interface';
import { useParams } from 'react-router-dom';
import { useHardwareDetailRelayQuery } from '../../hooks/hardware/query/V2/useHardwareDetailRelay';
import { useMaintenanceTasksRelayQuery } from '../../hooks/maintenance/query/V2/useMaintenanceTasksRelay';
import { useMaintenanceTicketByIdRelayQuery } from '../../hooks/maintenance/query/V2/useMaintenanceTicketsRelay';
import { mapMaintenanceTicketToLegacy } from '../../hooks/maintenance/query/V2/maintenanceMappers';
import { closeMaintenanceTicket } from '../../api/maintenance';
import { completeTask } from '../../api/problems';
import { getCurrentPosition } from '../../util/geolocation';
import { CLOSE_RESOLUTION_REASONS, MECHANIC_RENTAL_REQUIRED_PARTNERS } from './constants';
import { useMechanicRentalsLast24h } from '../../hooks/maintenance/query/useMechanicRentalsLast24h';
import MechanicRentalsList from '../../components/Maintenance/MechanicRentalsList';
import Box from '@mui/material/Box';
import { IAdmin } from '../../ts/interface/admins.interface';
import { IHardware } from '../../ts/interface/hardware.interface';
import { history, makeStationOptionsArray, toTitleCase } from '../../utils';
import { ITicket } from '../../ts/interface/problem.interface';
import useStation from '../../hooks/stations/query/useStation';
import TicketImageManager, {
  TicketImageManagerRef,
} from '../../components/Maintenance/TicketImageManager';

import IconButtonMenu from '../../components/Redesing/icon-button-menu';
import SaveIcon from '@mui/icons-material/Save';
import {
  MovaticFieldGroupAsyncSelect,
  MovaticFieldGroupOutlinedInput,
  MovaticFieldGroupSelect,
  MovaticFieldGroupText,
  MovaticFieldItemLink,
} from '../../components/Redesing/movatic-field-group';
import MenuItem from '@mui/material/MenuItem';
import PageContainer from '../../components/Redesing/page-container';
import CardView from '../../components/Redesing/card-view';
import { Stack } from '@mui/system';
import CustomAsyncToast from '../../components/Redesing/custom-async-toast';
import CustomToast from '../../components/Redesing/custom-toast';
import { LocationStatus } from '../../ts/enums';
import useOrganization from '../../hooks/use-organization';
import useHardware from '../../hooks/hardware/query/useHardware';
import type { MaintenanceTaskGraphQL } from '../../hooks/maintenance/query/V2/useMaintenanceTasksRelay';

interface IAddCompletedTaskState {
  time_tracking: string;
  time_trackingValidation: null | string;
  time_trackingHelp: string;
  notes: string;
  maintenance_task_id: string;
  hardware_id: string;
  type_name: string;
  module_id: string;
  closeTicket: boolean;
  hardwareOptions: { value: string; name: string }[];
  editHardware: boolean;
  hardwareNumber: string;
  station: null | string;
  ticket: ITicket | null;
  stationsOptions: { value: string; name: string; code: string }[];
  stationOpen: boolean;
  stationSearch: string;
  resolution_reason: string;
  resolution_details: string;
}

const AddCompletedTask = ({
  mode,
  system,
  systemLoaded,
  admin,
}: {
  mode: IMode;
  system: ISystem;
  systemLoaded: boolean;
  admin: IAdmin;
}) => {
  const { hardwareId = '', ticketId = '' } = useParams<{
    hardwareId: string;
    ticketId: string;
  }>();
  const imageManagerRef = useRef<TicketImageManagerRef>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const initialState = {
    time_tracking: '',
    time_trackingValidation: null,
    time_trackingHelp: '',
    notes: '',
    maintenance_task_id: '',
    hardware_id: '',
    type_name: '',
    module_id: '',
    closeTicket: !!ticketId,
    hardwareOptions: [],
    editHardware: true,
    hardwareNumber: '',
    station: null,
    ticket: null,
    stationsOptions: [] as { value: string; name: string; code: string }[],
    stationOpen: false,
    stationSearch: '',
    resolution_reason: '',
    resolution_details: '',
  };

  const [
    {
      time_tracking,
      time_trackingValidation,
      time_trackingHelp,
      notes,
      maintenance_task_id,
      hardware_id,
      type_name,
      closeTicket,
      hardwareOptions,
      editHardware,
      hardwareNumber,
      station,
      stationsOptions,
      stationOpen,
      stationSearch,
      resolution_reason,
      resolution_details,
    },
    setState,
  ] = useState<IAddCompletedTaskState>(initialState);

  const organizationInfo = useOrganization();

  const { data: hardware } = useHardwareDetailRelayQuery(hardwareId, {
    enabled: !!hardwareId,
  });

  const { data: getHardware = [], isLoading: isLoadingHardware } = useHardware(
    {
      enabled: !hardwareId && (systemLoaded || Boolean(organizationInfo?.id)),
    },
    { queryParameters: { organization_id: organizationInfo?.id } }
  );

  const allHardware = useMemo(() => {
    return getHardware?.data || [];
  }, [getHardware]);

  const { data: allTaskRaw = [] } = useMaintenanceTasksRelayQuery(
    {
      systemOldId: system?.id || undefined,
      organizationId: organizationInfo?.id || undefined,
      includeGlobal: true,
    },
    {
      enabled: systemLoaded || Boolean(organizationInfo?.id),
    }
  );

  // Map task data to use oldId as the id for compatibility
  const allTask = useMemo(
    () =>
      (allTaskRaw || []).map((task: MaintenanceTaskGraphQL) => ({
        id: task.oldId || task.id,
        gid: task.id,
        name: task.name,
        description: task.description,
        taskType: task.taskType,
      })),
    [allTaskRaw]
  );

  const { data: ticketDataRaw } = useMaintenanceTicketByIdRelayQuery(ticketId, {
    enabled: !!ticketId,
  });

  const ticketData = useMemo(
    () => (ticketDataRaw ? mapMaintenanceTicketToLegacy(ticketDataRaw) : null),
    [ticketDataRaw]
  );

  const {
    data: stationsData,
    isLoading: isLoadingStations,
    isRefetching: isRefetchingStations,
  } = useStation(
    { enabled: true },
    {
      status: LocationStatus.active,
      pattern: stationSearch,
      organization_id: organizationInfo?.id || '',
    }
  );

  useEffect(() => {
    const stations = stationsData?.data || [];
    if (stations.length > 0) {
      const formattedStations = makeStationOptionsArray(stations, '', true);
      setState((prevState) => ({
        ...prevState,
        stationsOptions: formattedStations,
      }));
    }
  }, [stationsData]);

  useEffect(() => {
    if (allTask.length > 0) {
      setState((prevState) => ({
        ...prevState,
        maintenance_task_id: allTask.length ? allTask[0].id : '',
      }));
    }
  }, [allTask]);

  const hardwareOptionsMemo = useMemo(() => {
    return allHardware
      ?.map((unit: IHardware) => ({
        value: unit.old_id,
        name: `${toTitleCase(unit.hardware_type.name)} ${unit.number || unit.old_id}`,
      }))
      .sort((a: { name: string }, b: { name: string }) =>
        a.name > b.name ? 1 : b.name > a.name ? -1 : 0
      );
  }, [allHardware]);

  useEffect(() => {
    if (hardwareId && hardware) {
      setState((prevState) => ({
        ...prevState,
        editHardware: false,
        hardware_id: hardware.oldId || hardware.id,
        hardwareNumber: hardware.number?.toString() || '',
        type_name: hardware.hardwareType?.name || '',
      }));
    }
  }, [hardwareId, hardware]);

  useEffect(() => {
    if (ticketId && ticketData) {
      setState((prevState) => ({
        ...prevState,
        ticket: ticketData,
        hardware_id: ticketData?.hardware_id,
        editHardware: true,
      }));
    }
  }, [ticketId, ticketData]);

  useEffect(() => {
    if (!hardwareId && !ticketId && hardwareOptionsMemo.length > 0) {
      setState((prevState) => {
        if (prevState.hardware_id !== hardwareOptionsMemo[0].value) {
          return {
            ...prevState,
            hardwareOptions: hardwareOptionsMemo,
            hardware_id: hardwareOptionsMemo[0].value,
          };
        }
        return prevState;
      });
    }
  }, [hardwareId, ticketId, hardwareOptionsMemo]);

  const requiresMechanicRental =
    closeTicket &&
    typeof system?.partner === 'number' &&
    MECHANIC_RENTAL_REQUIRED_PARTNERS.has(system.partner);

  const referenceTime = useMemo(() => new Date(), []);

  const { rentals: mechanicRentals, isLoading: mechanicRentalsLoading } =
    useMechanicRentalsLast24h(ticketData?.hardware_gid || undefined, referenceTime, {
      enabled: requiresMechanicRental && Boolean(ticketData?.hardware_gid),
    });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === 'hardware_id' && !system.new_hardware) {
      const unit: IHardware =
        allHardware?.find((hardware: IHardware) => hardware.id === e.target.value) ||
        ({} as IHardware);
      setState((prevState) => ({
        ...prevState,
        hardware_id: unit.id,
        module_id: unit?.module || '',
      }));
    } else if (e.target.name === 'time_tracking') {
      setState((prevState) => ({
        ...prevState,
        [e.target.name]: e.target.value,
        time_trackingValidation: null,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        [e.target.name]: e.target.value,
      }));
    }
  };

  const save = async () => {
    if (isNaN(Number(time_tracking)) || Number(time_tracking) <= 0 || !time_tracking) {
      setState((prevState) => ({
        ...prevState,
        time_trackingValidation: 'error',
        time_trackingHelp: 'Time taken must be a number greater than 0',
      }));
      return;
    }
    if (!closeTicket && maintenance_task_id === '') {
      CustomToast({
        type: 'error',
        message: 'Maintenance type cannot be empty',
      });
      return;
    }
    if (!closeTicket && hardware_id === '') {
      CustomToast({
        type: 'error',
        message: `${
          system.new_hardware ? 'Hardware' : mode.details.unitsTitle
        } cannot be empty`,
      });
      return;
    }

    if (closeTicket) {
      // Validate resolution reason
      if (!resolution_reason) {
        CustomToast({
          type: 'error',
          message: 'Please select a resolution reason.',
        });
        return;
      }

      setIsSubmitting(true);

      try {
        // Run GPS capture and image upload in parallel
        const [position, imagesUploaded] = await Promise.all([
          getCurrentPosition(),
          imageManagerRef.current
            ? imageManagerRef.current.uploadImages(ticketDataRaw?.id)
            : Promise.resolve(true),
        ]);

        if (!imagesUploaded) {
          CustomToast({
            type: 'error',
            message: 'Failed to upload images. Please try again.',
          });
          setIsSubmitting(false);
          return;
        }

        const result = await closeMaintenanceTicket({
          ticketId: ticketData?.id || ticketId,
          time_tracking: Number(time_tracking) * 60,
          notes: notes.toString() || '',
          station_gid: station || undefined,
          resolution_reason,
          resolution_details: resolution_details || undefined,
          resolution_lat: position?.lat,
          resolution_lng: position?.lng,
        });

        if (result) {
          CustomToast({
            type: 'success',
            message: 'Ticket closed successfully',
          });
          history.push(`/maintenance/tickets`);
        }
      } catch {
        CustomToast({
          type: 'error',
          message: 'Unable to close ticket',
        });
      } finally {
        setIsSubmitting(false);
      }
    } else {
      const task = {
        ticketId: ticketData?.id || null,
        time_tracking: Number(time_tracking) * 60,
        notes: notes.toString() || null,
        maintenance_task_id,
        hardware_id,
        station_gid: station || null,
      };

      CustomAsyncToast({
        promise: () => completeTask(task),
        successMessage: () => 'Log added successfully!',
        loadingMessage: 'Adding log...',
        errorMessage: 'Unable to add log',
      }).then((r) => {
        if (r) {
          history.push(`/maintenance/logs`);
        }
      });
    }
  };

  const goBack = () => {
    if (closeTicket) {
      history.push(`/maintenance/${ticketId}`);
    } else {
      history.goBack();
    }
  };

  const isLoading = !systemLoaded || !Boolean(organizationInfo?.id);

  return (
    <>
      <PageContainer
        isLoading={isLoading && isLoadingHardware}
        isSubmitting={isSubmitting}
      >
        {requiresMechanicRental && (
          <Box mb={2}>
            <MechanicRentalsList
              rentals={mechanicRentals}
              isLoading={mechanicRentalsLoading}
              ticketResolved={false}
            />
          </Box>
        )}
        <CardView
          title={closeTicket ? 'Close Ticket' : 'Log Maintenance'}
          content={
            <Stack px={2} pb={2}>
              {!closeTicket ? (
                <div>
                  {editHardware ? (
                    <MovaticFieldGroupSelect
                      label={system.new_hardware ? 'Hardware' : mode.details.unitsTitle}
                      description="Hardware that was worked on."
                      optionNodes={hardwareOptions.map(
                        (option: { value: string; name: string }, index: number) => (
                          <MenuItem key={index} value={option.value}>
                            {option.name}
                          </MenuItem>
                        )
                      )}
                      value={hardware_id}
                      onChange={handleChange}
                      name="hardware_id"
                    />
                  ) : system.new_hardware ? (
                    <>
                      <MovaticFieldItemLink
                        dataId={'hardware-id'}
                        onClick={() => history.push(`/hardware/${hardware_id}`)}
                        label={toTitleCase(type_name)}
                        content={hardware_id}
                      />
                    </>
                  ) : (
                    <MovaticFieldItemLink
                      dataId={'hardware-number'}
                      onClick={() => history.push(`/units/${hardware_id}`)}
                      label={mode.details.unitsTitle}
                      content={hardwareNumber}
                    />
                  )}

                  <MovaticFieldGroupAsyncSelect
                    isLoading={isLoadingStations || isRefetchingStations}
                    label={mode.details.stationsTitle}
                    description="(optional)"
                    name={'station'}
                    id={'station'}
                    open={stationOpen}
                    onOpen={() =>
                      setState((prevState) => ({
                        ...prevState,
                        stationOpen: true,
                      }))
                    }
                    onClose={() =>
                      setState((prevState) => ({
                        ...prevState,
                        stationOpen: false,
                      }))
                    }
                    isOptionEqualToValue={(option: any, value: any) =>
                      option.value === value.value
                    }
                    options={stationsOptions.map(
                      (station: { value: string; name: string }) => {
                        return { value: station.value, name: station.name };
                      }
                    )}
                    onChange={(event: any, newValue: { value: any }) =>
                      setState((prevState) => ({
                        ...prevState,
                        station: newValue?.value || null,
                      }))
                    }
                    getOptionText={(option: any) => `${option.name}`}
                    handleSearch={(search) =>
                      setState((prevState) => ({
                        ...prevState,
                        stationSearch: search.target.value,
                      }))
                    }
                  />

                  <MovaticFieldGroupSelect
                    optionNodes={allTask
                      ?.map((task: { id: string; name: string }) => ({
                        value: task.id,
                        name: task.name,
                      }))
                      .map((option: { value: string; name: string }, index: number) => (
                        <MenuItem key={index} value={option.value}>
                          {option.name}
                        </MenuItem>
                      ))}
                    onChange={handleChange}
                    name="maintenance_task_id"
                    label="Maintenance Type"
                    description="Maintenance type."
                    value={maintenance_task_id}
                  />
                </div>
              ) : null}
              <MovaticFieldGroupOutlinedInput
                type={'number'}
                label="Time Taken"
                description="Amount of time taken to complete task in minutes"
                value={time_tracking}
                inputProps={{ min: 1 }}
                hasError={time_trackingValidation === 'error'}
                errorMessage={time_trackingHelp}
                onChange={(event) => {
                  setState((prevState) => ({
                    ...prevState,
                    time_tracking: event.target.value,
                    time_trackingValidation: null,
                  }));
                }}
                name="time_tracking"
              />

              <MovaticFieldGroupText
                multiline
                maxRows={10}
                minRows={1}
                label="Additional Notes"
                placeholder="Notes"
                description="(Optional)"
                name="notes"
                value={notes}
                onChange={handleChange}
              />

              {closeTicket && (
                <>
                  <MovaticFieldGroupSelect
                    label="Resolution Reason"
                    description="Why is this ticket being closed?"
                    optionNodes={CLOSE_RESOLUTION_REASONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                    value={resolution_reason}
                    onChange={handleChange}
                    name="resolution_reason"
                  />
                  <MovaticFieldGroupText
                    multiline
                    maxRows={4}
                    minRows={1}
                    label="Resolution Details"
                    placeholder="Additional details about the resolution"
                    description="(Optional)"
                    name="resolution_details"
                    value={resolution_details}
                    onChange={handleChange}
                  />
                </>
              )}

              {closeTicket && (
                <TicketImageManager
                  ref={imageManagerRef}
                  ticketId={ticketDataRaw?.id || ''}
                  systemId={ticketData?.system_info?.old_id || ''}
                  existingImages={ticketData?.images || []}
                  readOnly={false}
                />
              )}
            </Stack>
          }
          headerActions={
            <IconButtonMenu
              buttons={[
                {
                  label: 'Cancel',
                  onClick: goBack,
                  disabled: isSubmitting,
                },
                {
                  label: 'Save',
                  onClick: save,
                  startIcon: <SaveIcon />,
                  disabled: isSubmitting,
                },
              ]}
            />
          }
        />
      </PageContainer>
    </>
  );
};

export default connect(
  (state: {
    system: { current: ISystem; isLoaded: boolean };
    mode: IMode;
    admin: { admin: { admin: IAdmin } };
  }) => ({
    system: state.system.current,
    systemLoaded: state.system.isLoaded,
    mode: state.mode,
    admin: state.admin.admin.admin,
  }),
  () => ({})
)(AddCompletedTask);
